import { BaseEdge, EdgeLabelRenderer, EdgeProps } from '@xyflow/react';
import { memo } from 'react';
import type { OwnershipEdge as OwnershipEdgeType } from '../graph/types';

function formatPercent(value: number): string {
  if (Number.isInteger(value)) return `${value}%`;
  // Round to 2 decimals, then strip trailing zeros: 33.333 → "33.33",
  // 12.50 → "12.5", 100 → "100".
  return `${value.toFixed(2).replace(/\.?0+$/, '')}%`;
}

type Endpoints = {
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
};

// Control points sit directly below the source and above the target, each a
// fixed fraction of the vertical gap away, so every edge leaves its parent and
// enters its child vertically and the curve stays symmetric about the midline.
const CURVE_TENSION = 0.5;

function curveSegment({
  sourceX,
  sourceY,
  targetX,
  targetY,
}: Readonly<Endpoints>): string {
  const reach = (targetY - sourceY) * CURVE_TENSION;
  return `C ${sourceX} ${sourceY + reach}, ${targetX} ${targetY - reach}, ${targetX} ${targetY}`;
}

// One vertical-tangent cubic per hop; consecutive segments share a point and
// both arrive/leave vertically, so the joins are smooth.
function curvePath(segments: readonly Endpoints[]): string {
  const [first] = segments;
  if (!first) return '';
  return `M ${first.sourceX} ${first.sourceY} ${segments.map(curveSegment).join(' ')}`;
}

// Splits an edge into hops through dagre's intermediate rank centres. Each
// waypoint carries the same lane offset as the handles so parallel edges stay
// parallel along the whole route.
function routeSegments(
  { sourceX, sourceY, targetX, targetY }: Readonly<Endpoints>,
  waypoints: readonly { x: number; y: number }[],
  offset: number,
): Endpoints[] {
  const stops = [
    { x: sourceX, y: sourceY },
    ...waypoints.map((p) => ({ x: p.x + offset, y: p.y })),
    { x: targetX, y: targetY },
  ];
  return stops.slice(1).map((to, i) => {
    const from = stops[i];
    return { sourceX: from.x, sourceY: from.y, targetX: to.x, targetY: to.y };
  });
}

// Chip position along its own curve, as a bezier parameter.
// - Straight drop (child directly under its parent): dead centre of the edge.
// - Otherwise near the child, where the curve has already straightened out, so
//   the chip reads as belonging to the child card below it rather than to a
//   sibling further along the row.
// - Each extra edge converging on the same child steps back up its own curve:
//   they all enter the child vertically, so equal parameters would stack the
//   chips on one point.
const CHIP_T_AT_CHILD = 0.78;
const CHIP_T_PER_SIBLING = 0.22;
const CHIP_T_MIN = 0.3;

function chipT(isStraightDrop: boolean, inIndex: number): number {
  if (isStraightDrop) return 0.5;
  return Math.max(CHIP_T_AT_CHILD - inIndex * CHIP_T_PER_SIBLING, CHIP_T_MIN);
}

// Horizontal spacing between parallel edges of the same (source, target)
// pair, centred on the shared handle. Wider than the 64px chip so lane chips
// on a straight drop (both at t=0.5) don't touch.
const PARALLEL_LANE_GAP = 72;

function laneOffset(parallelIndex: number, parallelCount: number): number {
  return (parallelIndex - (parallelCount - 1) / 2) * PARALLEL_LANE_GAP;
}

// Point on the cubic at `t`, so the chip always sits on the stroke it labels.
function curvePoint(
  { sourceX, sourceY, targetX, targetY }: Readonly<Endpoints>,
  t: number,
): { x: number; y: number } {
  const reach = (targetY - sourceY) * CURVE_TENSION;
  const u = 1 - t;
  const w0 = u ** 3;
  const w1 = 3 * u * u * t;
  const w2 = 3 * u * t * t;
  const w3 = t ** 3;
  return {
    x: w0 * sourceX + w1 * sourceX + w2 * targetX + w3 * targetX,
    y:
      w0 * sourceY +
      w1 * (sourceY + reach) +
      w2 * (targetY - reach) +
      w3 * targetY,
  };
}

const OwnershipEdge = memo(
  ({
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    data,
    style,
    markerEnd,
  }: EdgeProps<OwnershipEdgeType>) => {
    const offset = laneOffset(
      data?.parallelIndex ?? 0,
      data?.parallelCount ?? 1,
    );
    const segments = routeSegments(
      {
        sourceX: sourceX + offset,
        sourceY,
        targetX: targetX + offset,
        targetY,
      },
      data?.waypoints ?? [],
      offset,
    );
    const path = curvePath(segments);
    // The chip labels the child, so it sits on the final hop into it.
    const lastHop = segments[segments.length - 1];
    const { x: labelX, y: labelY } = curvePoint(
      lastHop,
      chipT(lastHop.sourceX === lastHop.targetX, data?.inIndex ?? 0),
    );

    const isControlOnly = data?.kind === 'control-only';

    return (
      <>
        <BaseEdge
          id={id}
          path={path}
          markerEnd={markerEnd}
          // Non-interactive: kill the wide interaction stroke so xyflow
          // doesn't paint pointer cursor over empty canvas.
          interactionWidth={0}
          style={{
            ...style,
            // CSS var so consumers (OrgDiagram, TaxDiagram) can override per
            // canvas. Fallback matches the chip border token.
            stroke: 'var(--ownership-edge-stroke, #DEDBD4)',
            strokeWidth: 2,
            ...(isControlOnly && { strokeDasharray: '4 4' }),
          }}
        />
        <EdgeLabelRenderer>
          <div
            className="absolute rounded-full border bg-bg-primary px-sm py-xs font-semibold text-text-primary text-center"
            style={{
              // Round to integer pixels so sub-pixel y from dagre doesn't
              // smear sibling labels onto adjacent rows during rasterization.
              transform: `translate(-50%, -50%) translate(${Math.round(labelX)}px, ${Math.round(labelY)}px)`,
              borderColor: 'var(--ownership-edge-stroke, #DEDBD4)',
              fontSize: 14,
              lineHeight: '20px',
              // Fixed width (not just min-width) so every chip occupies the
              // same rectangle regardless of label length. "100%" / "75%" /
              // "60%" all render with identical bbox, so chip tops align
              // pixel-perfect across a row of siblings.
              width: 64,
            }}
          >
            {formatPercent(data?.percent ?? 0)}
          </div>
        </EdgeLabelRenderer>
      </>
    );
  },
);
OwnershipEdge.displayName = 'OwnershipEdge';

export { OwnershipEdge };
